/**
 * Learning Center API: the training log PDF (plan docs/plans/lms-plan.md
 * 10.5, R16, R24, Appendix A "Certificates and the training log"; 18.19;
 * WP-35's route, WP-41's client).
 *
 *   GET /people/:userId/training-log.pdf  -> application/pdf, streamed, not
 *        stored, Cache-Control: private, no-store; own, or an admin who
 *        reaches the person (404 otherwise, 18.5); every generation audited
 *
 * The PDF is generated per request and never stored, so there is no
 * presigned URL to open: the bytes come back as a Blob (axios responseType
 * 'blob'), and the page saves them through an object URL and a download
 * anchor (saveBlob). An error answer then also arrives as a Blob holding the
 * JSON { error, code }; readBlobError turns it back into that body so
 * api/client.ts apiErrorMessage can read it.
 *
 * The certificate URL and the certificate retry are WP-40's
 * (api/completions.ts getCertificateUrl, retryCertificate; plan 18.22).
 */
import api from '../../services/api';

export interface TrainingLogFile {
  blob: Blob;
  filename: string;
}

const enc = encodeURIComponent;

/** The file name from a Content-Disposition header, else null. */
export function filenameFromDisposition(header: unknown): string | null {
  if (typeof header !== 'string' || !header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // fall through to the plain parameter
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/** The name used when the answer carries none: training-log-YYYY-MM-DD.pdf (the local date). */
export function defaultTrainingLogFilename(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `training-log-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.pdf`;
}

function readBlobText(blob: Blob): Promise<string> {
  const withText = blob as Blob & { text?: () => Promise<string> };
  if (typeof withText.text === 'function') return withText.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/**
 * When a blob request fails, its body is a Blob; replace it with the parsed
 * JSON so the error readers see { error, code }. Never throws itself.
 */
export async function readBlobError(err: unknown): Promise<unknown> {
  const e = err as { response?: { data?: unknown } } | null;
  const data = e?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob && e?.response) {
    try {
      const parsed = JSON.parse(await readBlobText(data));
      if (parsed && typeof parsed === 'object') e.response.data = parsed;
    } catch {
      // not JSON: leave the body as it was
    }
  }
  return err;
}

/**
 * GET /api/lms/people/:userId/training-log.pdf as a Blob (10.5). A learner
 * passes their own id; the route answers 404 for anyone else they ask for.
 */
export async function downloadTrainingLog(userId: string): Promise<TrainingLogFile> {
  try {
    const response = await api.get<Blob>(`/api/lms/people/${enc(userId)}/training-log.pdf`, { responseType: 'blob' });
    const headers = (response as { headers?: Record<string, unknown> }).headers || {};
    const data = response.data;
    const blob = data instanceof Blob ? data : new Blob([data as BlobPart], { type: 'application/pdf' });
    return {
      blob,
      filename: filenameFromDisposition(headers['content-disposition']) || defaultTrainingLogFilename(),
    };
  } catch (err) {
    throw await readBlobError(err);
  }
}

/**
 * Save a Blob as a file: an object URL on a download anchor, clicked, then
 * revoked once the browser has had time to start the download. The anchor
 * is never left in the page.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
